import { useEffect , useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Table, Column, AutoSizer } from 'react-virtualized';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { FaExpand, FaTrash } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import useDataSuiteWidgets from './useDataSuiteWidgets';
import formatters from '../../../config/formatters';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme } from '../../../config/constants';
import { isMobileOnly } from 'react-device-detect';

ChartJS.register(...registerables);

const TopProductsWidget = (props) => {
    const { w, i } = props;
    const { widgetTitle, widgetDetailsContent } = useDataSuiteWidgets.useTopProductsWidget();
    const { onClickWidgetDrawerOpen, onClickDeleteWidget, onClickWidgetDetailsDrawerOpen } = useDataSuite.useWidgetControls();
    const topProductsData = useSelector(({datasuite}) => datasuite.widgetData.topProductsData);
    const detailsDrawerTitle = useSelector(({datasuite}) => datasuite.widgetDetailsDrawer.title);
    const detailsDrawerOpen = useSelector(({ui}) => ui.datasuiteWidgetDetailsSlideDrawer);
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.topProductsLoading);
    const detailsIsLoading = useSelector(({datasuite}) => datasuite.detailsIsLoading.topProductsLoading);
    const { numberFormatter, percentFormatter } = formatters();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    // eslint-disable-next-line
    useEffect(() => {if(detailsDrawerOpen && (detailsDrawerTitle === widgetTitle)) {onClickWidgetDetailsDrawerOpen(widgetTitle, widgetDetailsContent());}}, [topProductsData, detailsIsLoading]);

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    {!isMobileOnly &&
                    <Tooltip title='Displays your sales by your relevant products. Helpful for identifying category growth opportunities as well as verifying compliance data. Information is up-to-date and updated daily.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}} />
                        </div>
                    </Tooltip>
                    }
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered) && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                    <Button
                        block
                        className={`py-1 button-label expandWidgetButton`}
                        size='sm'
                        outline={true}
                        color={Theme().colors.accentName}
                        onClick={() => onClickWidgetDetailsDrawerOpen(widgetTitle, widgetDetailsContent())}>
                        <FaExpand color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> Expand
                    </Button>
                </Col>
            </Row>
            {isLoading &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(topProductsData?.length <= 0) && !isLoading &&
            <Row className="mx-auto" style={{minHeight: '315px'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    <EmptyDataMessage />
                </Col>
            </Row>
            }
            {(topProductsData?.length > 0) && !isLoading &&
            <Row className="w-100 mb-3" style={{minHeight: '315px'}}>
                <Col className="col-12 text-size-14">
                    <Row style={{height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table topProductsTable border-grayLight" id="topProductsTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={topProductsData?.length}
                                    rowGetter={({ index }) => topProductsData[index]}
                                >
                                    <Column
                                    label='MIN'
                                    dataKey='min'
                                    width={width * 0.4}
                                    />
                                    <Column
                                    width={width * 0.4}
                                    label='Description'
                                    dataKey='description'
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='% of Sales'
                                    dataKey='sales_percent'
                                    cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default TopProductsWidget;
